/**
 * push_registration_controller.js — identifier "push-registration".
 *
 * The web half of push device registration: takes the device push token the
 * native shell hands to the page (via the bridge--notification-token
 * component) and POSTs it to /api/v1/push/devices so the server can address
 * OS pushes to this user's device. This is the sibling controller the
 * notification-token component's docstring expects — without it the whole
 * token chain is inert.
 *
 * Mounted TWICE:
 *   - base.html (never public_base.html: an unauthenticated page has no user
 *     to attach a token to): silent registration, plus the window-event
 *     wiring that lets the bell's push-prompt row (injected client-side by
 *     notification-ui.js when this device could enable push) trigger
 *     #enable with the click gesture intact (chippie:push-enable).
 *   - settings.html: the status targets (prompt/on/blocked/install/
 *     insecure/unsupported) that render the device's actual push state as
 *     an on/off control.
 * The POST rides the WebView's existing session cookie, so auth and tenancy
 * are already established; the API blueprint is CSRF-exempt like every
 * /api/* surface.
 *
 * Three intake paths, same POST:
 *   - silent (native): on connect, inside a native shell, ask the facade for
 *     the token (resolves only when the OS permission is already granted; a
 *     rejection is normal and expected — see notification_token_controller).
 *     Guarded by a sessionStorage flag so Turbo navigations don't re-POST on
 *     every page visit; the server only needs one last_seen_at refresh per
 *     launch.
 *   - asked once (native shells): when that silent check reports the
 *     permission as not decided, repeat it interactively so the OS shows its
 *     dialog. Neither shell prompts from the silent check, so without this a
 *     fresh install only gets push if the user finds the Settings toggle. The
 *     first page to run this is the first page after login. A localStorage
 *     flag keeps it to one automatic ask per install; iOS shows the dialog
 *     only once anyway, and Android stops after repeated refusals.
 *     A denial is recorded under NATIVE_DENIED_KEY for notification-ui.js,
 *     whose bell row sends the user to the OS settings, the only way back.
 *     Both states arrive as the bridge's platform-neutral TOKEN_* reasons;
 *     this controller never reads a shell's own wording.
 *   - silent (pwa): on connect in a browser, when Notification.permission is
 *     ALREADY granted, refresh the Web Push subscription (pushManager
 *     .subscribe with the VAPID public key from data-push-registration-vapid-
 *     key-value; no prompt can fire once permission is granted) and POST it
 *     as {platform:'web', token: endpoint, keys: {p256dh, auth}}. Skipped
 *     when the user explicitly turned push off (localStorage opt-out) —
 *     otherwise the next navigation would silently undo their choice.
 *   - explicit: the bridge button (data-action="bridge--notification-token
 *     :retrieved@window->push-registration#retrieved") on native, or
 *     data-action="push-registration#enable" on the PWA (settings block and
 *     nudge) — the one gesture allowed to show the browser's permission
 *     prompt.
 *
 * Turn off (#disable): unsubscribes the Web Push subscription, DELETEs the
 * device row, and sets the opt-out flag so silent registration stays off.
 * Browser permission itself cannot be revoked from a page — only settings
 * can — but an unsubscribed device receives nothing, which is what "off"
 * means to the user.
 *
 * Deregistration: the successful POST caches {platform, token} in
 * localStorage; a document-level submit listener (capture) watches for the
 * navbar's POST-to-/auth/logout forms and fires a keepalive DELETE with the
 * cached token before the session dies. DELETE is keyed by token, so signing
 * out one device never kills the user's other devices.
 */

import { Controller } from "@hotwired/stimulus";
import {
  TOKEN_DENIED,
  TOKEN_NOT_DETERMINED,
} from "./bridge/notification_token_controller.js";

const DEVICES_URL = "/api/v1/push/devices";
const CACHE_KEY = "chippie:push-device"; // JSON {platform, token}
const SESSION_FLAG = "chippie:push-registered"; // one silent attempt per session
const OPT_OUT_KEY = "chippie:push-opt-out"; // user pressed Turn off
const NATIVE_ASKED_KEY = "chippie:push-native-asked"; // automatic OS dialog already requested
// Read by notification-ui.js (bell row); this controller is the only writer.
const NATIVE_DENIED_KEY = "chippie:push-native-denied";

export default class extends Controller {
  static values = { vapidKey: String };
  static targets = [
    "prompt",
    "on",
    "blocked",
    "install",
    "insecure",
    "unsupported",
  ];

  connect() {
    this._onSubmit = (event) => this.#maybeDeregisterOnLogout(event);
    document.addEventListener("submit", this._onSubmit, true);
    this.#watchPermission();
    this.#renderStatus();
    this.#registerSilently();
  }

  disconnect() {
    document.removeEventListener("submit", this._onSubmit, true);
    if (this._permStatus) {
      this._permStatus.removeEventListener("change", this._onPermChange);
      this._permStatus = null;
    }
  }

  // data-action="bridge--notification-token:retrieved@window->push-registration#retrieved"
  retrieved(event) {
    const { token, environment } = event.detail || {};
    if (token) this.#post("native", token, environment);
  }

  // The PWA gesture that may show the browser's permission prompt (never
  // fired on page load). Reached two ways: the settings button
  // (data-action="push-registration#enable") and the bell's push-prompt row
  // (notification-ui.js dispatches chippie:push-enable synchronously from
  // its click, preserving the gesture; wired via data-action on the
  // base.html mount).
  enable(event) {
    if (event) event.preventDefault();
    if (!this.#webPushSupported()) return;
    localStorage.removeItem(OPT_OUT_KEY);
    Notification.requestPermission()
      .then((permission) => {
        if (permission === "granted") return this.#subscribeAndPost();
        return undefined;
      })
      .catch(() => {
        /* denied / dismissed: normal */
      })
      .finally(() => this.#renderStatus());
  }

  // data-action="push-registration#disable" — unsubscribe this device and
  // remember the choice so silent registration doesn't re-enrol it.
  disable(event) {
    if (event) event.preventDefault();
    localStorage.setItem(OPT_OUT_KEY, "1");
    localStorage.removeItem(CACHE_KEY);
    navigator.serviceWorker.ready
      .then((registration) => registration.pushManager.getSubscription())
      .then((subscription) => {
        if (!subscription) return undefined;
        const endpoint = subscription.endpoint;
        return subscription
          .unsubscribe()
          .then(() => this.#deleteDevice("web", endpoint));
      })
      .catch(() => {
        /* push service unreachable: the dead endpoint is pruned server-side */
      })
      .finally(() => this.#renderStatus());
  }

  #platform() {
    const bridge = window.ChippieBridge;
    return bridge && bridge._platform ? bridge._platform() : "pwa";
  }

  #registerSilently() {
    if (sessionStorage.getItem(SESSION_FLAG)) return;
    if (this.#platform() === "pwa") {
      // Browser path: refresh the subscription only when permission is
      // already granted — subscribing then cannot prompt. An explicit
      // Turn off (opt-out) wins over the refresh.
      if (!this.#webPushSupported() || Notification.permission !== "granted") return;
      if (localStorage.getItem(OPT_OUT_KEY)) return;
      sessionStorage.setItem(SESSION_FLAG, "1");
      this.#subscribeAndPost().catch(() => {
        /* push service unreachable: next session retries */
      });
      return;
    }
    sessionStorage.setItem(SESSION_FLAG, "1");
    window.ChippieBridge.notificationToken()
      .catch((error) => this.#askOnceIfUndecided(error))
      .then((result) => {
        // Contract: resolves {token, environment} (or a bare token string
        // from an older shell), null when unavailable.
        if (!result) return;
        const token = typeof result === "string" ? result : result.token;
        const environment =
          typeof result === "string" ? null : result.environment || null;
        if (token) this.#post("native", token, environment);
      })
      .catch((error) => {
        // Denial / unprovisioned: normal, nothing to register. Only a denial
        // is worth remembering, because only it has a recovery path.
        if (error && error.message === TOKEN_DENIED) {
          localStorage.setItem(NATIVE_DENIED_KEY, "1");
        }
      });
  }

  // Native shell, permission not decided: request the token again with
  // interactive set, which makes the shell show the OS dialog. Anything else
  // (a denial, an unprovisioned build, a second launch after already asking)
  // rethrows to the silent path's handler.
  #askOnceIfUndecided(error) {
    if (!error || error.message !== TOKEN_NOT_DETERMINED) throw error;
    if (localStorage.getItem(NATIVE_ASKED_KEY)) throw error;
    localStorage.setItem(NATIVE_ASKED_KEY, "1");
    return window.ChippieBridge.notificationToken({ interactive: true });
  }

  #pushApisPresent() {
    return (
      "Notification" in window &&
      "serviceWorker" in navigator &&
      "PushManager" in window
    );
  }

  #webPushSupported() {
    return this.#pushApisPresent() && !!this.vapidKeyValue;
  }

  /* -- Status rendering (settings block) --------------------------------- */

  // Resolve what this device can actually do right now. "hidden" means the
  // whole control is meaningless here (native shell, or a server with no
  // VAPID key). Every browser-side impossibility renders a VISIBLE state —
  // a blank block reads as a missing feature, especially in an installed
  // PWA where the user cannot force-refresh their way out.
  async #pushStatus() {
    if (this.#platform() !== "pwa" || !this.vapidKeyValue) return "hidden";
    if (!this.#pushApisPresent()) {
      // Push APIs only exist in secure contexts: a plain-HTTP origin can
      // never do Web Push, whatever the browser.
      if (!window.isSecureContext) return "insecure";
      // iOS Safari only exposes the Push API once the PWA is installed to
      // the home screen — "unsupported" there really means "not installed".
      return this.#isIosBrowserTab() ? "install" : "unsupported";
    }
    if (Notification.permission === "denied") return "blocked";
    if (Notification.permission === "default") return "prompt";
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    // Granted but unsubscribed (opt-out, or an expired endpoint): the prompt
    // button re-subscribes without any browser prompt.
    return subscription ? "on" : "prompt";
  }

  #statusTargets() {
    const targets = {};
    if (this.hasPromptTarget) targets.prompt = this.promptTarget;
    if (this.hasOnTarget) targets.on = this.onTarget;
    if (this.hasBlockedTarget) targets.blocked = this.blockedTarget;
    if (this.hasInstallTarget) targets.install = this.installTarget;
    if (this.hasInsecureTarget) targets.insecure = this.insecureTarget;
    if (this.hasUnsupportedTarget) targets.unsupported = this.unsupportedTarget;
    return targets;
  }

  #renderStatus() {
    const targets = this.#statusTargets();
    if (Object.keys(targets).length === 0) return; // base.html mount: nudge only
    this.#pushStatus()
      .then((status) => {
        Object.entries(targets).forEach(([name, el]) => {
          el.hidden = name !== status;
        });
      })
      .catch(() => {
        /* service worker never ready: leave everything hidden */
      });
  }

  // Live-update the settings block when the user flips the permission in
  // browser settings and comes back to the tab — no reload needed. A flip
  // to granted also re-runs silent registration so the subscription heals.
  #watchPermission() {
    if (!("permissions" in navigator)) return;
    navigator.permissions
      .query({ name: "notifications" })
      .then((status) => {
        this._permStatus = status;
        this._onPermChange = () => {
          if (Notification.permission === "granted") {
            sessionStorage.removeItem(SESSION_FLAG);
            this.#registerSilently();
          }
          this.#renderStatus();
        };
        status.addEventListener("change", this._onPermChange);
      })
      .catch(() => {
        /* Permissions API absent (older Safari): status renders on connect only */
      });
  }

  #isIosBrowserTab() {
    const ios =
      /iPhone|iPad|iPod/.test(navigator.userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    if (!ios) return false;
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    return !standalone;
  }

  async #subscribeAndPost() {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: this.#vapidKeyBytes(),
    });
    const { endpoint, keys } = subscription.toJSON();
    this.#post("web", endpoint, null, keys);
    this.#renderStatus();
  }

  // pushManager.subscribe wants the raw P-256 point, not the base64url string
  // the server exposes; Safari in particular rejects the string form.
  #vapidKeyBytes() {
    const base64 = this.vapidKeyValue.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const raw = atob(padded);
    return Uint8Array.from(raw, (ch) => ch.charCodeAt(0));
  }

  #post(kind, token, environment, keys) {
    const platform = kind === "web" ? "web" : this.#platform();
    if (kind !== "web" && platform === "pwa") return;
    // A native token means permission is granted now (Settings toggle, or the
    // user flipped it in the OS settings), so the bell's recovery row must go.
    if (kind === "native") localStorage.removeItem(NATIVE_DENIED_KEY);
    const body = { platform, token, environment: environment || null };
    if (keys) body.keys = keys;
    fetch(DEVICES_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    })
      .then((resp) => {
        if (!resp.ok) return;
        localStorage.setItem(CACHE_KEY, JSON.stringify({ platform, token }));
      })
      .catch(() => {
        /* offline / transient: next launch re-registers */
      });
  }

  #deleteDevice(platform, token, keepalive = false) {
    return fetch(DEVICES_URL, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, platform }),
      keepalive,
    }).catch(() => {
      /* best-effort: a dead token is pruned server-side on first failed send */
    });
  }

  #maybeDeregisterOnLogout(event) {
    const form = event.target;
    if (!form || !form.action || !form.action.includes("/auth/logout")) return;
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return;
    localStorage.removeItem(CACHE_KEY);
    sessionStorage.removeItem(SESSION_FLAG);
    let device;
    try {
      device = JSON.parse(cached);
    } catch (_e) {
      return;
    }
    // keepalive: survives the logout navigation tearing the page down.
    this.#deleteDevice(device.platform, device.token, true);
  }
}
